import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Send, Users, Trash2, CheckCircle, Clock, XCircle, Phone, Mail } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const AssignedCalls = () => {
  const navigate = useNavigate();
  const [studentEmails, setStudentEmails] = useState('');
  const [language, setLanguage] = useState('french');
  const [serviceType, setServiceType] = useState('random');
  const [sendEmailNotification, setSendEmailNotification] = useState(true);
  const [sending, setSending] = useState(false);
  const [assignedCalls, setAssignedCalls] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAssignedCalls();
  }, []);

  const fetchAssignedCalls = async () => {
    try {
      const response = await axios.get(`${API}/assigned-calls`);
      setAssignedCalls(response.data);
    } catch (error) {
      console.error('Error fetching assigned calls:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const sendAssignedCalls = async () => {
    if (!studentEmails.trim()) {
      toast.error('Veuillez entrer au moins un email étudiant');
      return;
    }

    // Parse emails (comma or newline separated)
    const emails = studentEmails
      .split(/[,\n]/)
      .map(e => e.trim())
      .filter(e => e && e.includes('@'));

    if (emails.length === 0) {
      toast.error('Aucun email valide trouvé');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${API}/assigned-calls`, {
        student_emails: emails,
        language: language,
        service_type: serviceType,
        assigned_by: 'Instructeur',
        send_email: sendEmailNotification
      });

      if (response.data.success) {
        let message = `${response.data.assigned_count} appel(s) assigné(s) avec succès!`;
        if (sendEmailNotification && response.data.emails_sent !== undefined) {
          message += ` ${response.data.emails_sent} email(s) envoyé(s).`;
        }
        toast.success(message);
        setStudentEmails('');
        fetchAssignedCalls();
      } else {
        toast.error(response.data.error || 'Erreur lors de l\'assignation');
      }
    } catch (error) {
      console.error('Error assigning calls:', error);
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSending(false);
    }
  };

  const deleteAssignedCall = async (callId) => {
    if (!window.confirm('Supprimer cet appel assigné?')) {
      return;
    }

    try {
      await axios.delete(`${API}/assigned-calls/${callId}`);
      toast.success('Appel supprimé');
      fetchAssignedCalls();
    } catch (error) {
      console.error('Error deleting assigned call:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const startAssignedCall = async (call) => {
    // Fetch the full scenario data first
    try {
      const scenarioResponse = await axios.get(`${API}/scenarios/${call.scenario_id}`);
      const scenarioData = scenarioResponse.data;
      
      // Store everything needed for TrainingChat (same pattern as RandomCall)
      sessionStorage.setItem('assigned_call_scenario', JSON.stringify(scenarioData));
      sessionStorage.setItem('assigned_call_id', call.id);
      sessionStorage.setItem('selected_training_language', call.language);
      sessionStorage.setItem('assigned_call_personality', call.personality);
      sessionStorage.setItem('assigned_call_mode', 'true');
      
      // Navigate to training with the assigned scenario
      navigate(`/training/${call.scenario_id}`);
    } catch (error) {
      console.error('Error fetching scenario:', error);
      toast.error('Erreur lors du chargement du scénario');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'pending':
        return <Clock className="w-5 h-5 text-orange-500" />;
      default:
        return <XCircle className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return 'Complété';
      case 'pending':
        return 'En attente';
      default:
        return 'Expiré';
    }
  };

  return (
    <div data-testid="assigned-calls-page" className="pt-20 px-4 sm:px-6 lg:px-8 pb-12 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-4xl sm:text-5xl font-bold mb-2" style={{ color: '#002768' }}>Appels Assignés</h1>
        <p className="text-lg text-gray-600">Envoyez des appels 911 aléatoires à vos étudiants</p>
      </div>

      {/* Assignment Form */}
      <Card className="glass border-0 mb-8">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Send className="w-5 h-5" style={{ color: '#2E6CF5' }} />
            <span>Assigner des Appels</span>
          </CardTitle>
          <CardDescription>
            Les étudiants recevront un appel aléatoire et verront une notification dans l'application
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Student Emails */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Emails des Étudiants
              </label>
              <textarea
                value={studentEmails}
                onChange={(e) => setStudentEmails(e.target.value)}
                placeholder="nom.prenom@cmontmorency.qc.ca&#10;autre.etudiant@cmontmorency.qc.ca&#10;(un email par ligne ou séparés par des virgules)"
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 resize-none"
                style={{ '--tw-ring-color': '#2E6CF5' }}
                rows="4"
              />
            </div>

            {/* Language Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Langue de l'Appel {serviceType !== 'random' && '(ignoré en mode aléatoire)'}
              </label>
              <div className="flex space-x-4">
                <button
                  onClick={() => setLanguage('french')}
                  disabled={serviceType === 'random'}
                  className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
                    language === 'french' && serviceType !== 'random'
                      ? 'bg-blue-500 text-white shadow-lg'
                      : serviceType === 'random'
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  🇫🇷 Français
                </button>
                <button
                  onClick={() => setLanguage('english')}
                  disabled={serviceType === 'random'}
                  className={`flex-1 py-3 rounded-lg font-semibold transition-all ${
                    language === 'english' && serviceType !== 'random'
                      ? 'bg-red-500 text-white shadow-lg'
                      : serviceType === 'random'
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  EN English
                </button>
              </div>
              {serviceType === 'random' && (
                <p className="text-xs text-amber-600 mt-2">
                  ⚠️ Mode aléatoire: Langue automatique (35% EN / 65% FR)
                </p>
              )}
            </div>

            {/* Service Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Type de Service
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <button
                  onClick={() => setServiceType('random')}
                  className={`py-3 px-4 rounded-lg font-semibold transition-all ${
                    serviceType === 'random'
                      ? 'bg-purple-500 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  🎲 Aléatoire
                </button>
                <button
                  onClick={() => setServiceType('police')}
                  className={`py-3 px-4 rounded-lg font-semibold transition-all ${
                    serviceType === 'police'
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Police
                </button>
                <button
                  onClick={() => setServiceType('ambulance')}
                  className={`py-3 px-4 rounded-lg font-semibold transition-all ${
                    serviceType === 'ambulance'
                      ? 'bg-red-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Ambulance
                </button>
                <button
                  onClick={() => setServiceType('incendie')}
                  className={`py-3 px-4 rounded-lg font-semibold transition-all ${
                    serviceType === 'incendie'
                      ? 'bg-red-700 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Incendie
                </button>
              </div>
              {serviceType === 'random' && (
                <p className="text-xs text-purple-600 mt-2">
                  🎲 Mode comme "Appels 911": Service ET langue aléatoires pour chaque étudiant
                </p>
              )}
            </div>

            {/* Email Notification Option */}
            <div className="flex items-center space-x-3 p-4 rounded-lg border" style={{ backgroundColor: '#CAE9FA', borderColor: '#2E6CF5' }}>
              <input
                type="checkbox"
                id="sendEmail"
                checked={sendEmailNotification}
                onChange={(e) => setSendEmailNotification(e.target.checked)}
                className="w-5 h-5 border-gray-300 rounded"
                style={{ accentColor: '#2E6CF5' }}
              />
              <label htmlFor="sendEmail" className="flex items-center space-x-2 cursor-pointer">
                <Mail className="w-5 h-5" style={{ color: '#2E6CF5' }} />
                <span className="text-gray-700 font-medium">
                  Envoyer une notification par email aux étudiants
                </span>
              </label>
            </div>

            {/* Send Button */}
            <button
              onClick={sendAssignedCalls}
              disabled={sending}
              className="w-full py-4 text-white rounded-lg font-bold text-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              style={{ background: 'linear-gradient(to right, #002768, #2E6CF5)' }}
            >
              {sending ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Envoi en cours...</span>
                </div>
              ) : (
                <div className="flex items-center justify-center space-x-2">
                  <Send className="w-5 h-5" />
                  <span>Envoyer les Appels</span>
                </div>
              )}
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Assigned Calls List */}
      <Card className="glass border-0">
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Users className="w-5 h-5" style={{ color: '#2E6CF5' }} />
            <span>Appels Assignés ({assignedCalls.length})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin mx-auto mb-4" style={{ borderColor: '#2E6CF5', borderTopColor: 'transparent' }}></div>
              <p className="text-gray-600">Chargement...</p>
            </div>
          ) : assignedCalls.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">Aucun appel assigné pour le moment</p>
            </div>
          ) : (
            <div className="space-y-3">
              {assignedCalls.map((call) => (
                <div
                  key={call.id}
                  className="flex items-center justify-between p-4 bg-white rounded-lg border-2 border-gray-200 hover:border-blue-300 transition-all"
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      {getStatusIcon(call.status)}
                      <span className="font-semibold" style={{ color: '#002768' }}>{call.student_email}</span>
                      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                        call.language === 'french' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {call.language === 'french' ? '🇫🇷 FR' : 'EN'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Scénario: {call.scenario_title || 'Chargement...'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Assigné le: {new Date(call.assigned_date).toLocaleDateString('fr-FR', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      call.status === 'completed' ? 'bg-green-100 text-green-700' :
                      call.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {getStatusLabel(call.status)}
                    </span>
                    {call.status === 'pending' && (
                      <>
                        <button
                          onClick={() => startAssignedCall(call)}
                          className="px-4 py-2 text-white rounded-lg font-semibold hover:shadow-lg transition-all flex items-center space-x-2"
                          style={{ background: 'linear-gradient(to right, #24B219, #82F486)' }}
                          title="Démarrer l'appel"
                        >
                          <Phone className="w-4 h-4" />
                          <span>Répondre</span>
                        </button>
                        <button
                          onClick={() => deleteAssignedCall(call.id)}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AssignedCalls;
